import { useAuthStore } from '../store/authStore';
import { useCallStore } from '../store/callStore';
import { wsClient } from './websocket';
import { hostFromServerUrl, serverKeyFromServerUrl } from '../utils/serverKey';
import type { PublicKey } from '@shared/types';
import type { SigningKey } from '../crypto/ed25519Provider';

export type CallActionContext = {
  localIdentityId: string;
  localDeviceId: string;
  localServerId: string;
  localServerUrl?: string;
  signingKey: SigningKey;
  deviceSigningKey: SigningKey;
  ecdhKey: CryptoKey | null;
  ecdhScalar: Uint8Array;
  targetIdentityId: string;
  targetServerId?: string;
  targetServerUrl?: string;
};

export type MessageActionContext = {
  localIdentityId: string;
  localDeviceId: string;
  localServerId: string;
  localServerUrl?: string;
  recipientIdentityId: string;
  signingKey: SigningKey;
  ecdhKey: CryptoKey | null;
  ecdhScalar: Uint8Array;
};

export type MessagingLocalContext = {
  localIdentityId: string;
  localDeviceId: string;
  localServerId: string;
  localServerUrl?: string;
  localPublicKey: PublicKey;
  signingKey: SigningKey;
  ecdhKey: CryptoKey | null;
  ecdhScalar: Uint8Array;
};

type ResolvedLocalContext = {
  identityId: string;
  deviceId: string;
  serverId: string;
  serverUrl?: string;
  accessLevel: 'trusted' | 'temporary';
  signingKey: SigningKey;
  ecdhKey: CryptoKey | null;
  ecdhScalar: Uint8Array;
  publicKey?: PublicKey;
};

function strictContextError(code: string, details: Record<string, unknown>): Error {
  const error = new Error(code);
  (error as Error & { code?: string; details?: Record<string, unknown> }).code = code;
  (error as Error & { code?: string; details?: Record<string, unknown> }).details = details;
  return error;
}

function isUnspecifiedServerId(serverId: string | null | undefined): boolean {
  const normalized = (serverId || '').toString().trim().toLowerCase();
  return normalized === '' || normalized === 'default' || normalized === 'default-server' || normalized === 'unknown';
}

function getActiveServerId(): string | null {
  try {
    const state = useAuthStore.getState();
    const currentDeviceId = (state.currentDeviceId || '').toString().trim();
    if (!currentDeviceId) return null;
    const device = state.devices[currentDeviceId];
    if (!device) return null;
    const identity = state.identities[device.identityId];
    const id = (identity?.serverId || '').toString().trim();
    return id || null;
  } catch {
    return null;
  }
}

function hasCallInProgress(): boolean {
  try {
    const state = useCallStore.getState();
    return Boolean(state.activeCall || state.incomingCall);
  } catch {
    return false;
  }
}

function assertCanSwitchMessagingDevice(local: ResolvedLocalContext): void {
  if (!hasCallInProgress()) return;

  const state = useAuthStore.getState();
  if (state.currentDeviceId === local.deviceId) return;
  throw strictContextError('CALL_IN_PROGRESS_WS_LOCKED', {
    requestedIdentityId: local.identityId,
    requestedDeviceId: local.deviceId,
    currentDeviceId: state.currentDeviceId
  });
}

async function waitForCurrentServerId(expectedServerId: string, timeoutMs = 10_000): Promise<boolean> {
  const expected = (expectedServerId || '').toString().trim();
  if (!expected) return true;

  const active = getActiveServerId();
  if (active === expected) return true;

  return await new Promise((resolve) => {
    let done = false;
    const startedAt = Date.now();

    const finish = (value: boolean) => {
      if (done) return;
      done = true;
      resolve(value);
    };

    const unsubscribe = useAuthStore.subscribe(() => {
      const now = getActiveServerId();
      if (now === expected) {
        try {
          unsubscribe();
        } catch {
          // ignore
        }
        finish(true);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        try {
          unsubscribe();
        } catch {
          // ignore
        }
        finish(false);
      }
    });

    window.setTimeout(() => {
      try {
        unsubscribe();
      } catch {
        // ignore
      }
      finish(false);
    }, timeoutMs + 50);
  });
}

async function waitForWsReadyForServer(serverUrl?: string, timeoutMs = 10_000): Promise<boolean> {
  const expectedUrl = (serverUrl || '').toString().trim();

  if (expectedUrl) {
    if (wsClient.isConnectedToServer(expectedUrl)) return true;
  } else if (wsClient.isConnected()) {
    return true;
  }

  wsClient.connect();

  return await new Promise((resolve) => {
    let done = false;
    const startedAt = Date.now();

    const finish = (value: boolean) => {
      if (done) return;
      done = true;
      resolve(value);
    };

    const timer = window.setInterval(() => {
      const connected = expectedUrl
        ? wsClient.isConnectedToServer(expectedUrl)
        : wsClient.isConnected();
      if (connected) {
        window.clearInterval(timer);
        finish(true);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(timer);
        finish(false);
      }
    }, 100);

    window.setTimeout(() => {
      window.clearInterval(timer);
      finish(false);
    }, timeoutMs + 200);
  });
}

function identityMatchesServerHint(identity: { serverId?: string; serverUrl?: string }, targetServerId?: string, targetServerUrl?: string): boolean {
  const byServerId = (identity.serverId || '').toString().trim();
  const byServerUrl = (identity.serverUrl || '').toString().trim();

  const requestedId = (targetServerId || '').toString().trim();
  const requestedUrl = (targetServerUrl || '').toString().trim();

  const requestedKey = serverKeyFromServerUrl(requestedUrl || requestedId) || requestedId;
  const requestedHost = hostFromServerUrl(requestedUrl) || hostFromServerUrl(requestedId) || hostFromServerUrl(requestedKey);

  if (requestedKey) {
    if (byServerId === requestedKey || byServerId === requestedId) return true;
    if (serverKeyFromServerUrl(byServerId) === requestedKey) return true;
    if (serverKeyFromServerUrl(byServerUrl) === requestedKey) return true;
  }

  if (requestedHost) {
    const byHost = hostFromServerUrl(byServerUrl) || hostFromServerUrl(byServerId);
    if (byHost && byHost === requestedHost) return true;
  }

  return false;
}

function resolveLocalIdentityForCall(targetServerId?: string, targetServerUrl?: string, localIdentityId?: string): {
  identityId: string;
  deviceId: string;
  serverId: string;
  serverUrl?: string;
  accessLevel: 'trusted' | 'temporary';
  signingKey: SigningKey;
  deviceSigningKey: SigningKey;
  ecdhKey: CryptoKey | null;
  ecdhScalar: Uint8Array;
} {
  const state = useAuthStore.getState();
  const identities = Object.values(state.identities);
  const devices = Object.values(state.devices);

  const requestedId = (targetServerId || '').toString().trim();
  const requestedUrl = (targetServerUrl || '').toString().trim();
  const requestedLocalIdentityId = (localIdentityId || '').toString().trim();
  const hasServerHint = !!requestedUrl || (!!requestedId && !isUnspecifiedServerId(requestedId));

  const activeDeviceId = state.currentDeviceId;
  const activeDevice = activeDeviceId ? state.devices[activeDeviceId] : null;
  const activeIdentity = activeDevice ? state.identities[activeDevice.identityId] : null;

  const pickByIdentity = (identityId: string) => {
    const identity = state.identities[identityId];
    if (!identity) return null;
    const device = devices.find((d) => d.identityId === identityId);
    if (!device) return null;
    if (!identity.signingKey || !device.signingKey) return null;
    return {
      identityId: identity.identityId,
      deviceId: device.deviceId,
      serverId: identity.serverId,
      serverUrl: identity.serverUrl,
      accessLevel: 'trusted' as const,
      signingKey: identity.signingKey,
      deviceSigningKey: device.signingKey,
      ecdhKey: identity.ecdhKey,
      ecdhScalar: identity.ecdhScalar
    };
  };

  if (requestedLocalIdentityId) {
    const picked = pickByIdentity(requestedLocalIdentityId);
    if (!picked) {
      throw strictContextError('EXPLICIT_LOCAL_IDENTITY_NOT_FOUND', {
        requestedLocalIdentityId,
        knownIdentityIds: identities.map((i) => i.identityId)
      });
    }

    if (hasServerHint && !identityMatchesServerHint(state.identities[picked.identityId], requestedId, requestedUrl)) {
      throw strictContextError('EXPLICIT_LOCAL_IDENTITY_SERVER_MISMATCH', {
        requestedLocalIdentityId,
        requestedId,
        requestedUrl,
        identityServerId: state.identities[picked.identityId]?.serverId || null,
        identityServerUrl: state.identities[picked.identityId]?.serverUrl || null
      });
    }

    return picked;
  }

  if (hasServerHint) {
    const matchedIdentity = identities.find((i) => identityMatchesServerHint(i, requestedId, requestedUrl));
    if (matchedIdentity) {
      const picked = pickByIdentity(matchedIdentity.identityId);
      if (picked) return picked;
    }

    throw strictContextError('LOCAL_IDENTITY_NOT_FOUND_FOR_TARGET_SERVER', {
      requestedId,
      requestedUrl,
      identities: identities.map((i) => ({ identityId: i.identityId, serverId: i.serverId, serverUrl: i.serverUrl || null }))
    });
  }

  if (identities.length > 1) {
    throw strictContextError('AMBIGUOUS_LOCAL_IDENTITY_FOR_CALL', {
      identityCount: identities.length,
      activeIdentityId: activeIdentity?.identityId || null,
      requestedId,
      requestedUrl
    });
  }

  if (identities.length === 1) {
    const picked = pickByIdentity(identities[0].identityId);
    if (picked) return picked;
  }

  throw new Error('NO_LOCAL_IDENTITY_CONTEXT');
}

function resolveLocalIdentityForMessage(localIdentityId?: string): {
  identityId: string;
  deviceId: string;
  serverId: string;
  serverUrl?: string;
  accessLevel: 'trusted' | 'temporary';
  publicKey: PublicKey;
  signingKey: SigningKey;
  ecdhKey: CryptoKey | null;
  ecdhScalar: Uint8Array;
} {
  const state = useAuthStore.getState();
  const identities = Object.values(state.identities);
  const devices = Object.values(state.devices);
  const temporaryMemberships = Object.values(state.temporaryMemberships);

  const pickByIdentity = (identityId: string) => {
    const identity = state.identities[identityId];
    if (!identity) return null;
    const device = devices.find((d) => d.identityId === identityId);
    if (!device) return null;
    if (!identity.signingKey) return null;
    return {
      identityId: identity.identityId,
      deviceId: device.deviceId,
      serverId: identity.serverId,
      serverUrl: identity.serverUrl,
      accessLevel: 'trusted' as const,
      publicKey: identity.publicKey,
      signingKey: identity.signingKey,
      ecdhKey: identity.ecdhKey,
      ecdhScalar: identity.ecdhScalar
    };
  };

  const pickTemporaryByIdentity = (identityId: string) => {
    const membership = temporaryMemberships.find((item) => item.identityId === identityId);
    if (!membership) return null;

    return {
      identityId: membership.identityId,
      deviceId: membership.deviceId,
      serverId: serverKeyFromServerUrl(membership.serverUrl) || membership.serverUrl,
      serverUrl: membership.serverUrl,
      accessLevel: 'temporary' as const,
      publicKey: membership.publicKey,
      signingKey: membership.signingKey,
      ecdhKey: membership.ecdhKey,
      ecdhScalar: membership.ecdhScalar
    };
  };

  const requestedLocalIdentityId = (localIdentityId || '').toString().trim();
  if (requestedLocalIdentityId) {
    const picked = pickByIdentity(requestedLocalIdentityId) || pickTemporaryByIdentity(requestedLocalIdentityId);
    if (picked) return picked;

    throw strictContextError('EXPLICIT_LOCAL_IDENTITY_NOT_FOUND', {
      requestedLocalIdentityId,
      knownIdentityIds: [
        ...identities.map((i) => i.identityId),
        ...temporaryMemberships.map((membership) => membership.identityId)
      ]
    });
  }

  if (identities.length > 1) {
    throw strictContextError('AMBIGUOUS_LOCAL_IDENTITY_FOR_MESSAGE', {
      identityCount: identities.length,
      activeIdentityId: null
    });
  }

  if (identities.length === 1) {
    const picked = pickByIdentity(identities[0].identityId);
    if (picked) return picked;
  }

  if (temporaryMemberships.length === 1) {
    const picked = pickTemporaryByIdentity(temporaryMemberships[0].identityId);
    if (picked) return picked;
  }

  throw new Error('NO_LOCAL_IDENTITY_CONTEXT');
}

function resolveExplicitLocalIdentityForMessage(localIdentityId: string): {
  identityId: string;
  deviceId: string;
  serverId: string;
  serverUrl?: string;
  accessLevel: 'trusted' | 'temporary';
  publicKey: PublicKey;
  signingKey: SigningKey;
  ecdhKey: CryptoKey | null;
  ecdhScalar: Uint8Array;
} {
  const requestedLocalIdentityId = (localIdentityId || '').toString().trim();
  if (!requestedLocalIdentityId) {
    throw strictContextError('MISSING_LOCAL_IDENTITY', {});
  }
  return resolveLocalIdentityForMessage(requestedLocalIdentityId);
}

async function alignLocalContext(local: ResolvedLocalContext, timeoutMs: number): Promise<void> {
  if (local.accessLevel === 'temporary') {
    if (!local.serverUrl) {
      throw new Error('TEMPORARY_SERVER_URL_MISSING');
    }
    if (!(wsClient.isTemporaryMode() && wsClient.isConnectedToServer(local.serverUrl))) {
      await wsClient.connectTemporary({
        serverUrl: local.serverUrl,
        deviceId: local.deviceId,
        signingKey: local.signingKey,
        timeoutMs
      });
    }
    return;
  }

  const state = useAuthStore.getState();
  if (state.currentDeviceId !== local.deviceId) {
    assertCanSwitchMessagingDevice(local);
    state.setCurrentDevice(local.deviceId);
  }

  const serverAligned = await waitForCurrentServerId(local.serverId || '', timeoutMs);
  if (!serverAligned) {
    throw new Error('SERVER_CONTEXT_SWITCH_TIMEOUT');
  }

  const wsReady = await waitForWsReadyForServer(local.serverUrl, timeoutMs);
  if (!wsReady) {
    throw new Error('WS_NOT_READY_FOR_SERVER');
  }
}

async function alignCallLocalContext(local: ResolvedLocalContext, timeoutMs: number): Promise<void> {
  if (local.accessLevel === 'temporary') {
    if (!local.serverUrl) {
      throw new Error('TEMPORARY_SERVER_URL_MISSING');
    }
    if (!(wsClient.isTemporaryMode() && wsClient.isConnectedToServer(local.serverUrl))) {
      await wsClient.connectTemporary({
        serverUrl: local.serverUrl,
        deviceId: local.deviceId,
        signingKey: local.signingKey,
        timeoutMs
      });
    }
    return;
  }

  const state = useAuthStore.getState();
  if (state.currentDeviceId !== local.deviceId) {
    state.setCurrentDevice(local.deviceId);
  }
}

export async function resolveCallActionContext(params: {
  targetIdentityId: string;
  targetServerId?: string;
  targetServerUrl?: string;
  localIdentityId?: string;
  timeoutMs?: number;
}): Promise<CallActionContext> {
  const targetIdentityId = (params.targetIdentityId || '').toString().trim();
  if (!targetIdentityId) {
    throw new Error('MISSING_TARGET_IDENTITY');
  }

  const timeoutMs = Math.max(3_000, params.timeoutMs || 10_000);
  const local = resolveLocalIdentityForCall(params.targetServerId, params.targetServerUrl, params.localIdentityId);
  await alignCallLocalContext(local, timeoutMs);

  return {
    localIdentityId: local.identityId,
    localDeviceId: local.deviceId,
    localServerId: local.serverId,
    localServerUrl: local.serverUrl,
    signingKey: local.signingKey,
    deviceSigningKey: local.deviceSigningKey,
    ecdhKey: local.ecdhKey,
    ecdhScalar: local.ecdhScalar,
    targetIdentityId,
    targetServerId: params.targetServerId,
    targetServerUrl: params.targetServerUrl
  };
}

export async function resolveStrictMessageActionContext(params: {
  recipientIdentityId: string;
  localIdentityId: string;
  timeoutMs?: number;
}): Promise<MessageActionContext> {
  const recipientIdentityId = (params.recipientIdentityId || '').toString().trim();
  if (!recipientIdentityId) {
    throw new Error('MISSING_RECIPIENT_IDENTITY');
  }

  const timeoutMs = Math.max(3_000, params.timeoutMs || 10_000);
  const local = resolveExplicitLocalIdentityForMessage(params.localIdentityId);
  await alignLocalContext(local, timeoutMs);

  return {
    localIdentityId: local.identityId,
    localDeviceId: local.deviceId,
    localServerId: local.serverId,
    localServerUrl: local.serverUrl,
    recipientIdentityId,
    signingKey: local.signingKey,
    ecdhKey: local.ecdhKey,
    ecdhScalar: local.ecdhScalar
  };
}

export async function resolveStrictHttpMessageActionContext(params: {
  recipientIdentityId: string;
  localIdentityId: string;
}): Promise<MessageActionContext> {
  const recipientIdentityId = (params.recipientIdentityId || '').toString().trim();
  if (!recipientIdentityId) {
    throw new Error('MISSING_RECIPIENT_IDENTITY');
  }

  const local = resolveExplicitLocalIdentityForMessage(params.localIdentityId);
  if (local.accessLevel === 'temporary' && !local.serverUrl) {
    throw new Error('TEMPORARY_SERVER_URL_MISSING');
  }

  const state = useAuthStore.getState();
  if (local.accessLevel === 'trusted' && state.currentDeviceId !== local.deviceId) {
    assertCanSwitchMessagingDevice(local);
    state.setCurrentDevice(local.deviceId);
  }

  return {
    localIdentityId: local.identityId,
    localDeviceId: local.deviceId,
    localServerId: local.serverId,
    localServerUrl: local.serverUrl,
    recipientIdentityId,
    signingKey: local.signingKey,
    ecdhKey: local.ecdhKey,
    ecdhScalar: local.ecdhScalar
  };
}

export async function resolveStrictMessagingLocalContext(params: {
  localIdentityId: string;
  timeoutMs?: number;
}): Promise<MessagingLocalContext> {
  const timeoutMs = Math.max(3_000, params.timeoutMs || 10_000);
  const local = resolveExplicitLocalIdentityForMessage(params.localIdentityId);
  await alignLocalContext(local, timeoutMs);

  return {
    localIdentityId: local.identityId,
    localDeviceId: local.deviceId,
    localServerId: local.serverId,
    localServerUrl: local.serverUrl,
    localPublicKey: local.publicKey,
    signingKey: local.signingKey,
    ecdhKey: local.ecdhKey,
    ecdhScalar: local.ecdhScalar
  };
}
